/**
 * Configuración de PostHog para Analytics
 * 
 * Variables de entorno requeridas:
 * - VITE_POSTHOG_KEY: API Key de PostHog
 * - VITE_POSTHOG_HOST: Host de PostHog (ej: https://us.i.posthog.com)
 * 
 * En producción, configurar en Vercel Dashboard > Settings > Environment Variables
 */

import posthog from 'posthog-js';
import { logger } from './logger';

let posthogInitialized = false;

/**
 * Inicializar PostHog en el cliente
 */
export function initPostHog() {
  if (posthogInitialized) {
    return;
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
  const environment = import.meta.env.MODE || 'development';

  // Solo inicializar si hay API key
  if (!apiKey) {
    if (environment === 'production') {
      logger.warn('PostHog API key no configurado. Analytics deshabilitado.');
    }
    posthogInitialized = true;
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    loaded: (posthog) => {
      if (environment === 'development') {
        // En desarrollo, habilitar debug mode
        posthog.debug();
      }
    },
    // Configuración de privacidad
    autocapture: true, // Captura automática de eventos (clicks, form submissions)
    capture_pageview: true, // Captura automática de page views
    capture_pageleave: true, // Captura de cuando el usuario abandona la página
    // GDPR: No usar cookies persistentes por defecto
    persistence: 'localStorage+cookie', // Puedes cambiar a 'localStorage' o 'memory' si prefieres más privacidad
    // Identificación automática de usuarios (si están autenticados)
    disable_session_recording: false, // Habilitar session replay (útil para debugging)
    session_recording: {
      maskAllInputs: true, // Ocultar inputs en grabaciones (privacidad)
      maskAllText: false, // Mantener texto visible (útil para UX)
      maskTextSelector: '[data-sensitive]', // Selector para elementos sensibles
    },
  });

  posthogInitialized = true;
}

/**
 * Identificar usuario (cuando se autentica)
 */
export function identifyUser(userId: string, userProperties?: Record<string, any>) {
  if (!posthogInitialized || !import.meta.env.VITE_POSTHOG_KEY) return;
  
  posthog.identify(userId, userProperties);
}

/**
 * Resetear identificación (cuando el usuario cierra sesión)
 */
export function resetUser() {
  if (!posthogInitialized || !import.meta.env.VITE_POSTHOG_KEY) return;
  
  posthog.reset();
}

/**
 * Trackear evento personalizado
 */
export function trackEvent(eventName: string, properties?: Record<string, any>) {
  if (!posthogInitialized || !import.meta.env.VITE_POSTHOG_KEY) return;
  
  posthog.capture(eventName, properties);
}

/**
 * Exportar instancia de PostHog (para uso avanzado)
 */
export { posthog };

